import { ChangeEvent, useState } from "react";
import { Check, CheckCircle2, Copy, Loader2 } from "lucide-react";
import { useEnroll } from "@/api/hooks";
import { type EnrollResponse, type TenantPlan } from "@/api/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// ── Plan definitions ──────────────────────────────────────────────────────────

interface PlanInfo {
    id: TenantPlan;
    price: string;
    userLimit: string;
    maxUsers: number | null;
    apps: string[];
    recommended?: boolean;
    ctaNote?: string;
}

const plans: PlanInfo[] = [
    {
        id: "Starter",
        price: "$9/user/month",
        userLimit: "Up to 10 users",
        maxUsers: 10,
        apps: ["Mail", "Calendar"]
    },
    {
        id: "Business",
        price: "$15/user/month",
        userLimit: "Up to 50 users",
        maxUsers: 50,
        apps: ["Mail", "Calendar", "Docs", "Sheets"],
        recommended: true
    },
    {
        id: "Enterprise",
        price: "$25/user/month",
        userLimit: "Unlimited users",
        maxUsers: null,
        apps: ["Mail", "Calendar", "Docs", "Sheets", "Drive", "Teams"],
        ctaNote: "Contact sales for pricing"
    }
];

// ── Step indicator ────────────────────────────────────────────────────────────

const STEPS = ["Company Details", "Select Your Plan", "Review & Submit"] as const;

function StepIndicator({ current }: { current: number }) {
    return (
        <nav aria-label="Enrollment steps" className="mb-8">
            <ol className="flex items-center gap-0">
                {STEPS.map((label, i) => {
                    const step = i + 1;
                    const done = step < current;
                    const active = step === current;
                    return (
                        <li key={label} className="flex flex-1 items-center">
                            <div className="flex flex-col items-center gap-1">
                                <span
                                    className={cn(
                                        "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold border-2 transition-colors",
                                        done
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : active
                                                ? "border-primary bg-primary/10 text-primary"
                                                : "border-muted bg-muted/40 text-muted-foreground"
                                    )}
                                >
                                    {done ? <Check className="h-4 w-4" /> : step}
                                </span>
                                <span className={cn(
                                    "text-xs font-medium whitespace-nowrap",
                                    active ? "text-foreground" : "text-muted-foreground"
                                )}>
                                    {label}
                                </span>
                            </div>
                            {i < STEPS.length - 1 && (
                                <div className={cn(
                                    "mx-2 h-0.5 flex-1 mb-5 transition-colors",
                                    done ? "bg-primary" : "bg-muted"
                                )} />
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

// ── Form field helper ─────────────────────────────────────────────────────────

function FormField({
    id,
    label,
    required,
    hint,
    children
}: {
    id: string;
    label: string;
    required?: boolean;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label htmlFor={id}>
                {label}
                {required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
            {children}
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
    );
}

// ── Step 1 — Company Details ──────────────────────────────────────────────────

interface CompanyDetails {
    companyName: string;
    domain: string;
    adminName: string;
    adminEmail: string;
    phone: string;
    address: string;
}

function Step1({
    values,
    onChange,
    onNext
}: {
    values: CompanyDetails;
    onChange: (field: keyof CompanyDetails, value: string) => void;
    onNext: () => void;
}) {
    function handleChange(field: keyof CompanyDetails) {
        return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onChange(field, e.target.value);
    }

    const isValid =
        values.companyName.trim() &&
        values.domain.trim() &&
        values.adminName.trim() &&
        values.adminEmail.trim();

    return (
        <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
                <FormField id="companyName" label="Company Name" required>
                    <Input
                        id="companyName"
                        placeholder="Acme Corporation"
                        required
                        value={values.companyName}
                        onChange={handleChange("companyName")}
                    />
                </FormField>
                <FormField
                    id="domain"
                    label="Domain"
                    required
                    hint='Your email will be user@yourdomain.com — enter the domain only, e.g. "acmecorp.com"'
                >
                    <Input
                        id="domain"
                        placeholder="acmecorp.com"
                        required
                        value={values.domain}
                        onChange={handleChange("domain")}
                    />
                </FormField>
                <FormField id="adminName" label="Contact Name" required>
                    <Input
                        id="adminName"
                        placeholder="Jane Smith"
                        required
                        value={values.adminName}
                        onChange={handleChange("adminName")}
                    />
                </FormField>
                <FormField id="adminEmail" label="Contact Email" required>
                    <Input
                        id="adminEmail"
                        type="email"
                        placeholder="jane@acmecorp.com"
                        required
                        value={values.adminEmail}
                        onChange={handleChange("adminEmail")}
                    />
                </FormField>
                <FormField id="phone" label="Contact Phone">
                    <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 555 000 0000"
                        value={values.phone}
                        onChange={handleChange("phone")}
                    />
                </FormField>
                <FormField id="address" label="Company Address">
                    <Input
                        id="address"
                        placeholder="123 Main St, City, Country"
                        value={values.address}
                        onChange={handleChange("address")}
                    />
                </FormField>
            </div>

            <div className="flex justify-end pt-2">
                <Button onClick={onNext} disabled={!isValid}>
                    Continue to Plan Selection
                </Button>
            </div>
        </div>
    );
}

// ── Step 2 — Select Plan ──────────────────────────────────────────────────────

function PlanCard({
    plan,
    selected,
    onSelect
}: {
    plan: PlanInfo;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <div
            className={cn(
                "relative flex cursor-pointer flex-col rounded-xl border-2 p-5 transition-colors",
                selected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/20"
            )}
            onClick={onSelect}
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
        >
            {plan.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                    RECOMMENDED
                </span>
            )}
            <div className="mb-3 flex items-start justify-between">
                <h3 className="text-lg font-semibold">{plan.id}</h3>
                {selected && <Check className="h-5 w-5 shrink-0 text-primary" />}
            </div>
            <p className="text-2xl font-bold text-primary">{plan.price}</p>
            <p className="mt-1 text-xs text-muted-foreground">{plan.userLimit}</p>
            {plan.ctaNote && (
                <p className="mt-1 text-xs text-muted-foreground italic">{plan.ctaNote}</p>
            )}
            <ul className="mt-4 space-y-1.5">
                {plan.apps.map((app) => (
                    <li key={app} className="flex items-center gap-2 text-sm">
                        <Check className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
                        {app}
                    </li>
                ))}
            </ul>
            <Button
                type="button"
                variant={selected ? "default" : "outline"}
                size="sm"
                className="mt-5"
                onClick={(e) => { e.stopPropagation(); onSelect(); }}
            >
                {selected ? "Selected" : "Select"}
            </Button>
        </div>
    );
}

function Step2({
    selectedPlan,
    userCount,
    onPlanChange,
    onUserCountChange,
    onBack,
    onNext
}: {
    selectedPlan: TenantPlan | null;
    userCount: number | null;
    onPlanChange: (plan: TenantPlan) => void;
    onUserCountChange: (n: number | null) => void;
    onBack: () => void;
    onNext: () => void;
}) {
    const planInfo = plans.find((p) => p.id === selectedPlan);

    return (
        <div className="space-y-6">
            <div
                role="radiogroup"
                aria-label="Subscription plan"
                className="grid gap-4 sm:grid-cols-3"
            >
                {plans.map((p) => (
                    <PlanCard
                        key={p.id}
                        plan={p}
                        selected={selectedPlan === p.id}
                        onSelect={() => {
                            onPlanChange(p.id);
                            // Reset user count when plan changes
                            onUserCountChange(p.maxUsers ? Math.min(userCount ?? 1, p.maxUsers) : null);
                        }}
                    />
                ))}
            </div>

            {/* User count */}
            {selectedPlan && planInfo && (
                <div className="rounded-lg border bg-muted/20 p-4">
                    <FormField
                        id="userCount"
                        label="Number of Users"
                        hint={planInfo.maxUsers ? `Maximum ${planInfo.maxUsers} users for ${planInfo.id} plan` : "Enterprise plan supports unlimited users"}
                    >
                        {planInfo.id === "Enterprise" ? (
                            <Input
                                id="userCount"
                                type="number"
                                min={1}
                                placeholder="Enter number of users"
                                value={userCount ?? ""}
                                onChange={(e) =>
                                    onUserCountChange(e.target.value ? parseInt(e.target.value, 10) : null)
                                }
                                className="max-w-[200px]"
                            />
                        ) : (
                            <Input
                                id="userCount"
                                type="number"
                                min={1}
                                max={planInfo.maxUsers ?? undefined}
                                value={userCount ?? ""}
                                onChange={(e) =>
                                    onUserCountChange(e.target.value ? parseInt(e.target.value, 10) : null)
                                }
                                className="max-w-[200px]"
                            />
                        )}
                    </FormField>
                </div>
            )}

            <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={onBack}>Back</Button>
                <Button onClick={onNext} disabled={!selectedPlan}>
                    Continue to Review
                </Button>
            </div>
        </div>
    );
}

// ── Step 3 — Review & Submit ──────────────────────────────────────────────────

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-4 py-2 text-sm border-b last:border-0">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium text-right">{value}</span>
        </div>
    );
}

function Step3({
    company,
    selectedPlan,
    userCount,
    agreedTerms,
    onTermsChange,
    onBack,
    onSubmit,
    isSubmitting,
    error
}: {
    company: CompanyDetails;
    selectedPlan: TenantPlan;
    userCount: number | null;
    agreedTerms: boolean;
    onTermsChange: (v: boolean) => void;
    onBack: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    error: string | null;
}) {
    const planInfo = plans.find((p) => p.id === selectedPlan)!;
    const pricePerUser = selectedPlan === "Starter" ? 9 : selectedPlan === "Business" ? 15 : 25;
    const users = userCount ?? 1;
    const estimatedMonthly = users * pricePerUser;

    return (
        <div className="space-y-5">
            {error && (
                <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                </div>
            )}

            {/* Summary */}
            <div className="rounded-lg border bg-card p-5 shadow-sm">
                <h3 className="mb-3 font-semibold">Company Information</h3>
                <div className="divide-y">
                    <ReviewRow label="Company Name" value={company.companyName} />
                    <ReviewRow label="Domain" value={<span className="font-mono">{company.domain}</span>} />
                    <ReviewRow label="Contact Name" value={company.adminName} />
                    <ReviewRow label="Contact Email" value={company.adminEmail} />
                    {company.phone && <ReviewRow label="Phone" value={company.phone} />}
                    {company.address && <ReviewRow label="Address" value={company.address} />}
                </div>
            </div>

            <div className="rounded-lg border bg-card p-5 shadow-sm">
                <h3 className="mb-3 font-semibold">Selected Plan</h3>
                <div className="divide-y">
                    <ReviewRow label="Plan" value={planInfo.id} />
                    <ReviewRow label="Price" value={planInfo.price} />
                    <ReviewRow label="Users" value={planInfo.id === "Enterprise" ? (userCount ?? "Unlimited") : (userCount ?? "—")} />
                    <ReviewRow
                        label="Included Apps"
                        value={planInfo.apps.join(", ")}
                    />
                    <ReviewRow
                        label="Estimated First Invoice"
                        value={
                            <span className="text-lg font-bold text-primary">
                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(estimatedMonthly)}
                                <span className="text-xs font-normal text-muted-foreground"> /month</span>
                            </span>
                        }
                    />
                </div>
            </div>

            {/* Terms */}
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border bg-muted/20 p-4">
                <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => onTermsChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span className="text-sm leading-relaxed">
                    I agree to the{" "}
                    <a href="#" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        Terms of Service
                    </a>{" "}
                    and acknowledge that an invoice will be generated upon enrollment approval.
                </span>
            </label>

            <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={onBack} disabled={isSubmitting}>Back</Button>
                <Button onClick={onSubmit} disabled={!agreedTerms || isSubmitting}>
                    {isSubmitting
                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                        : "Submit Enrollment"}
                </Button>
            </div>
        </div>
    );
}

// ── Success view ──────────────────────────────────────────────────────────────

function SuccessView({
    result,
    onEnrollAnother
}: {
    result: EnrollResponse;
    onEnrollAnother: () => void;
}) {
    const amount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: result.currency
    }).format(result.amount);

    return (
        <div className="flex flex-col items-center gap-6 py-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
                <h2 className="text-2xl font-semibold">Enrollment submitted successfully!</h2>
                <p className="mt-2 text-muted-foreground">
                    Invoice <span className="font-mono font-semibold">{result.invoiceNumber}</span> has been generated for{" "}
                    <span className="font-semibold">{amount}</span>.
                </p>
            </div>

            <div className="w-full max-w-md rounded-lg border bg-card p-5 text-left shadow-sm">
                <h3 className="mb-3 font-semibold">What happens next?</h3>
                <ol className="space-y-3">
                    {[
                        "Our team will review your enrollment (typically within 1 business day).",
                        `You'll receive an invoice by email (Invoice ${result.invoiceNumber} — ${amount}).`,
                        "Once payment is confirmed, you'll receive your admin credentials.",
                        "Publish the DNS records below on your domain registrar."
                    ].map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                {i + 1}
                            </span>
                            {step}
                        </li>
                    ))}
                </ol>
            </div>

            {result.dnsRecords?.length ? (
                <DnsGuidance records={result.dnsRecords} />
            ) : null}

            <Button variant="outline" onClick={onEnrollAnother}>
                Enroll another organisation
            </Button>
        </div>
    );
}

function DnsGuidance({
    records
}: {
    records: EnrollResponse["dnsRecords"];
}) {
    function copy(text: string) {
        void navigator.clipboard.writeText(text).then(
            () => { /* no-op — visual feedback comes from the icon transition */ },
            () => { window.prompt("Copy manually:", text); }
        );
    }

    return (
        <div className="w-full max-w-3xl rounded-lg border bg-card p-5 text-left shadow-sm">
            <h3 className="mb-1 font-semibold">DNS records to publish</h3>
            <p className="mb-4 text-xs text-muted-foreground">
                Add these records on your domain registrar's DNS zone. Mail delivery,
                SPF / DKIM signing, and Outlook autodiscover all depend on the values
                being exact — copy them rather than retyping.
            </p>
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b bg-muted/40 text-left font-medium text-muted-foreground">
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Value</th>
                            <th className="px-3 py-2 text-right">Copy</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((r, i) => (
                            <tr key={i} className="border-b last:border-0 align-top">
                                <td className="px-3 py-2 font-mono font-semibold">{r.recordType}</td>
                                <td className="px-3 py-2 font-mono">{r.name}</td>
                                <td className="px-3 py-2 font-mono break-all">{r.expectedValue}</td>
                                <td className="px-3 py-2 text-right">
                                    <button
                                        type="button"
                                        onClick={() => copy(r.expectedValue)}
                                        className="inline-flex items-center gap-1 rounded p-1 hover:bg-accent"
                                        aria-label={`Copy ${r.recordType} value`}
                                        title="Copy value"
                                    >
                                        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
                Once your registrar publishes these records (TTL propagation can take up
                to an hour), sign in to the admin portal and click <em>Verify DNS</em>
                on your tenant page — successful verification activates outbound mail.
            </p>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

interface FormState {
    companyName: string;
    domain: string;
    adminName: string;
    adminEmail: string;
    phone: string;
    address: string;
    notes: string;
    selectedPlan: TenantPlan | null;
    userCount: number | null;
    agreedTerms: boolean;
}

function initialFormState(): FormState {
    return {
        companyName: "",
        domain: "",
        adminName: "",
        adminEmail: "",
        phone: "",
        address: "",
        notes: "",
        selectedPlan: null,
        userCount: 5,
        agreedTerms: false
    };
}

export function EnrollPage() {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState<FormState>(initialFormState());
    const [result, setResult] = useState<EnrollResponse | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const enrollMutation = useEnroll();

    function setField<K extends keyof FormState>(field: K, value: FormState[K]) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    async function handleSubmit() {
        if (!form.selectedPlan) return;
        setSubmitError(null);
        const planInfo = plans.find((p) => p.id === form.selectedPlan)!;
        try {
            const res = await enrollMutation.mutateAsync({
                companyName: form.companyName,
                domain: form.domain,
                adminEmail: form.adminEmail,
                adminName: form.adminName,
                phone: form.phone || undefined,
                address: form.address || undefined,
                plan: form.selectedPlan,
                maxUsers: form.userCount ?? planInfo.maxUsers ?? undefined,
                enabledApps: planInfo.apps,
                notes: form.notes || undefined
            });
            setResult(res);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : "Enrollment failed. Please try again.");
        }
    }

    function resetForm() {
        setStep(1);
        setForm(initialFormState());
        setResult(null);
        setSubmitError(null);
    }

    const companyValues: CompanyDetails = {
        companyName: form.companyName,
        domain: form.domain,
        adminName: form.adminName,
        adminEmail: form.adminEmail,
        phone: form.phone,
        address: form.address
    };

    return (
        <div className="min-h-screen bg-muted/30 py-10 px-4">
            <div className="mx-auto max-w-3xl">
                {/* Page header */}
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-bold">Enroll Your Organisation</h1>
                    <p className="mt-2 text-muted-foreground">
                        Start your Athavita Enterprise journey — set up your private suite in minutes.
                    </p>
                </div>

                <div className="rounded-xl border bg-card p-6 shadow-md sm:p-8">
                    {result ? (
                        <SuccessView result={result} onEnrollAnother={resetForm} />
                    ) : (
                        <>
                            <StepIndicator current={step} />

                            {step === 1 && (
                                <Step1
                                    values={companyValues}
                                    onChange={(field, value) => setField(field as keyof FormState, value)}
                                    onNext={() => setStep(2)}
                                />
                            )}

                            {step === 2 && (
                                <Step2
                                    selectedPlan={form.selectedPlan}
                                    userCount={form.userCount}
                                    onPlanChange={(plan) => setField("selectedPlan", plan)}
                                    onUserCountChange={(n) => setField("userCount", n)}
                                    onBack={() => setStep(1)}
                                    onNext={() => setStep(3)}
                                />
                            )}

                            {step === 3 && form.selectedPlan && (
                                <Step3
                                    company={companyValues}
                                    selectedPlan={form.selectedPlan}
                                    userCount={form.userCount}
                                    agreedTerms={form.agreedTerms}
                                    onTermsChange={(v) => setField("agreedTerms", v)}
                                    onBack={() => setStep(2)}
                                    onSubmit={() => void handleSubmit()}
                                    isSubmitting={enrollMutation.isPending}
                                    error={submitError}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
